/**
 * The single-team report on /rate's "Rate my team" tab.
 *
 * Same family as ReportView, and the same rule: every claim names its own data.
 * The benchmark label, the 1QB small-sample line and the 2QB closest-cell note
 * ship verbatim from the league report, and every count comes off the artifact
 * meta rather than out of prose that goes stale the next time the harvest runs.
 *
 * 🔴 THE YARDSTICK IS THE CORPUS, NOT A ROOM, AND THE COPY HAS TO SAY SO.
 * The league report can write "2nd of 12" because it has eleven neighbours. A
 * pasted team has none, so every relative claim here is against real 2026
 * auction teams and is worded that way.
 *
 * 🔴 WITHOUT PRICES THERE IS NO GRADE AND NO SENTENCE THAT SOUNDS LIKE ONE.
 * Projections and positional strength stand on their own; a letter, a surplus,
 * a best buy and a worst overpay all require prices, and when they are absent
 * the page says what is missing rather than quietly grading a roster on half
 * the evidence.
 *
 * STRUCTURE ADOPTED FROM FPTS FRONT OFFICE (design addendum, Aug 30): the
 * roster renders as one graded card per position group, starters first and
 * then a bench section, each card wearing a grade badge with its percentile as
 * the number. What is NOT adopted is the part that makes their numbers opaque:
 * the floor stays D rather than F, every badge number is a corpus percentile
 * with its cell named in the fine print, and nothing here claims a title
 * chance or a win probability, because we have no season model to back one.
 *
 * 🔴 THE HERO CLIP IS ATMOSPHERE, NEVER CONTENT. At most one video plays on
 * this page, ever. Since Aug 30 the backdrop CYCLES through every rostered
 * player who has a clip (starters first, priced by what was paid and then
 * unpriced by projected points, then the bench - see heroOrder), but
 * one at a time and through one mounted element: the swap is a dissolve through
 * the card, only the next file is preloaded, and a dead id drops out of the
 * rotation silently. Rows carry faces and nothing else, entry carries no video
 * at all, and the element only mounts once the report renders. Only 46 players
 * have a clip, so the card is designed for the NO-clip case first and the
 * backdrop is something it gains, never something it needs. Reduced motion, a
 * dead Giphy id and a player nobody filmed all land on the same finished static
 * card.
 *
 * Dwell and crossfade are CLIP_LOOK dials, not literals in this file. That is
 * the same rule the live board's flash follows and the reason clipLook.js
 * exists: the right value is a judgement Ben makes looking at the page.
 *
 * The backdrop reuses ClipBackdrop, the live board's CARD treatment, rather
 * than restating its dim and scrim here. That component is also deliberately
 * UNBLURRED: clipLook.js documents the split ("why the flash is blurred and the
 * card is not") on the grounds that a ~600px card is near the clips' native
 * width, and this card is the same ~600px, so the same reasoning applies.
 * Blurring it would have meant borrowing flashBlurPx, a dial documented as
 * belonging to the full-page flash, or inventing a literal.
 *
 * NO SHARE LINK IN V1. No report is stored, so there is no URL that would bring
 * this one back. The page says nothing about sharing rather than offering a
 * link that cannot work.
 *
 * 🔴 THE EMAIL GATE, AND WHERE THE LINE IS (round 5, Aug 30 2026). The line
 * moved UP, to the top of the report. Ben: "before you see the animation and
 * everything. Just a peek at maybe the projected points in week 1 and the
 * percentage."
 *
 * FREE is now one compact teaser card carrying two claims and nothing else:
 * the week 1 projected points, and the percentile line that says which real
 * population they beat. No meter, no clips, no chips, no market line, no grade
 * badge. GATED is everything else, the hero card included.
 *
 * 🔴 THE ANIMATION IS THE REWARD, WHICH IS WHY IT IS BEHIND THE LINE. The
 * meters sweep once per mount, so mounting them post-unlock means the moment a
 * reader gives an address the report assembles and the corpus climbs in front
 * of them. Showing the sweep first and then asking would spend the payoff
 * before the ask.
 *
 * Two rules this gate keeps, unchanged from round 4. First, THE GATED CONTENT
 * IS REAL AND ALREADY COMPUTED: the report object holds every card before the
 * panel renders, so the promise is a promise about something that exists.
 * Second, the gated sections are ABSENT FROM THE DOM rather than hidden, so
 * nothing is "unlockable" with a devtools inspector and, more to the point,
 * nobody can accidentally ship a gate that is only a visual one. The locked
 * page is its own early return, which is the strongest form of that guarantee.
 *
 * 🔴 ONE ASK, AND THE ROSTER STAYS EDITABLE. The report used to carry a second
 * email card at the bottom; with the gate at the top that card was a second ask
 * on a page whose reader has already answered one, so it is gone and the league
 * report keeps its own. What the teaser DOES keep is "Edit my roster", because
 * that is navigation and not report content: a reader who mistyped a name must
 * be able to go back and fix it without paying an address for the privilege.
 *
 * The unlock is once ever per browser, not once per report: somebody who gave
 * an address for one roster has bought the breakdown.
 *
 * 🔴 THE LEAGUE REPORT IS NOT GATED AND MUST NOT BE. /rate?league= is the share
 * loop: one person rates a room and twelve league mates open the link. A gate
 * there taxes the growth engine, so ReportView has nothing to do with any of
 * this.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { CLIP_LOOK } from "../../data/clipLook.js";
import { theme } from "../../data/theme.js";
import {
  captureRateGateShown,
  captureRateGateUnlocked,
  captureTeamRated,
} from "../../utils/rateAnalytics.js";
import { isRateUnlocked, markRateUnlocked } from "../../utils/rateTeamStore.js";
import { clipFor } from "../../utils/playerClips.js";
import { KD_PROJECTIONS_2026 } from "../../data/kdProjections2026.js";
import { GRADE_POSITIONS, projectionIndex } from "../../utils/rateProjection.js";
import { posRankOf } from "../../utils/rateTeam.js";
import { avatarPlayer } from "./avatarPlayer.js";
import GroupCard, {
  GRADE_COLOR,
  NONE,
  groupTitleColor,
  money,
  signedMoney,
} from "./GroupCard.jsx";
import useCountUp from "../../hooks/useCountUp.js";
import { RATE_MOTION } from "../../data/rateMotion.js";
// prefersReducedMotion is already imported below, straight from NominationFlash,
// which is the one definition of the media query; rateMotion only re-exports it.
import { chipEnter, gradePop } from "./rateMotion.js";
import { ClipBackdrop } from "../LiveBoard/NominationBlock.jsx";
import { prefersReducedMotion } from "../LiveBoard/NominationFlash.jsx";
import PlayerAvatar from "../PlayerAvatar.jsx";
import { StarterSplitLine } from "./SpendBreakdown.jsx";
import { starterBenchSpend } from "../../utils/leagueSpend.js";
import { C, SZ, card, fineText, leagueNameText } from "../Submit/submitStyles.js";
import CorpusMeter, {
  benchPercentileLine,
  fmtCount,
  pctShown,
  percentileClaim,
  percentileLine,
} from "./CorpusMeter.jsx";
import EmailAsk from "./EmailAsk.jsx";
import LineupChips from "./LineupChips.jsx";
import { SlotChip } from "./TeamEntryLadder.jsx";

/** Re-exported from its own module so the shared card can use it too. */
export { avatarPlayer };

/**
 * How many NFL teams the K/DEF table covers, counted off the table itself.
 *
 * 🔴 EVERY COUNT ON THIS PAGE COMES FROM ITS OWN ARTIFACT. A prose "32" would
 * be right today and silently wrong the first time somebody hand-refreshed the
 * table, which is exactly how the player count in the sentence beside it is
 * handled (projectionIndex().size) and why the corpus counts come off
 * report.corpus rather than out of the copy.
 */
const KD_TEAM_COUNT = Object.keys(KD_PROJECTIONS_2026).length;


/**
 * The roster in the order the report leads with it: what was paid for, most
 * expensive first, then everyone else by projected points, then the bench.
 *
 * 🔴 PRICE FIRST, BECAUSE PRICE IS WHAT THE OWNER REMEMBERS (Aug 30, round 4).
 * A projected-points order opens the report on whoever a model likes best,
 * which is often not the player the room fought over and paid up for. The face
 * of a team is the man its owner spent on. Projected points stay as the order
 * for everybody unpriced, so a roster entered without prices is ordered exactly
 * as it was before this rule existed.
 *
 * A price TIE breaks on projected points, which is the mirror of the rule this
 * replaced (points, ties broken by price) and keeps the order deterministic on
 * a roster of $1 bench bodies.
 *
 * The bench keeps roster order: a bench is depth, and ranking it by what was
 * paid would open the second half of the cycle on whichever backup cost the
 * most, which is not a fact about the team worth leading with.
 *
 * ONE FUNCTION, TWO CONSUMERS, ON PURPOSE. The headline ("led by") and the clip
 * cycle both read this, so the name on the card and the first face on screen
 * agree by construction rather than by two orderings happening to match.
 *
 * @returns { starters, bench } - picks off report.picks, already ordered
 */
export function heroOrder(report) {
  const key = (p) => `${p.player}|${p.pos}`;
  const ppg = new Map();
  for (const s of (report.projection && report.projection.starters) || []) {
    ppg.set(key(s), s.ppg);
  }
  const picks = report.picks || [];
  const started = picks.filter((p) => ppg.has(key(p)));
  const bench = picks.filter((p) => !ppg.has(key(p)));
  const byPpg = (a, b) => (ppg.get(key(b)) || 0) - (ppg.get(key(a)) || 0);
  const priced = started
    .filter((p) => p.paid != null)
    .sort((a, b) => b.paid - a.paid || byPpg(a, b));
  const unpriced = started.filter((p) => p.paid == null).sort(byPpg);
  return { starters: [...priced, ...unpriced], bench };
}

/**
 * The face of the team: the first STARTER in that order.
 *
 * Starters rather than the whole roster, because a bench player who happens to
 * be expensive or to project well is not what the team is about.
 *
 * Null when nothing was projected, and the card is built to look finished that
 * way: only 46 players in the set have a clip and most rosters will surface
 * none, so the no-clip and no-hero paths are the normal ones, not the edge.
 */
export function heroPick(report) {
  return heroOrder(report).starters[0] || null;
}

/**
 * Every rostered player who has a clip, in the order the backdrop plays them:
 * the same order as the headline, filtered to the players somebody filmed.
 *
 * Ben: "show more clips from the other players on the squad, they should
 * cycle." Starters lead because the backdrop should open on somebody the owner
 * paid for and is starting, and since Aug 30 the first frame is the most
 * EXPENSIVE such starter WITH footage - which is usually, but not always, the
 * same man the headline names, because only 46 players have a clip at all.
 *
 * 🔴 THE HEADLINE DOES NOT FOLLOW THE CYCLE. "led by <hero>" is a claim about
 * the roster and stays put; the caption in the corner is what tracks the
 * footage. If the headline changed every eight seconds it would read as eight
 * different claims about the team.
 *
 * Empty for a roster nobody filmed, which is the normal case.
 */
export function heroCycle(report) {
  const { starters, bench } = heroOrder(report);
  return [...starters, ...bench]
    .map((pick) => ({ pick, clip: clipFor(pick.player) }))
    .filter((f) => f.clip)
    .map((f) => ({ pick: f.pick, url: f.clip.url, focus: f.clip.focus }));
}

/**
 * The hero backdrop, cycling one roster clip at a time.
 *
 * 🔴 STILL EXACTLY ONE VIDEO ON THE PAGE. The swap is a dissolve THROUGH the
 * card, not a cross-dissolve between two overlapping videos: the whole backdrop
 * fades to nothing over heroCrossfadeMs, the source changes, and it fades back.
 * A second mounted video would double the bandwidth on a page most people open
 * on a phone and break the guarantee the rest of this file is built on.
 *
 * 🔴 ONLY THE NEXT CLIP IS PRELOADED, NEVER THE SET. The preloader is a
 * DETACHED video element: it warms the next file and is never appended, so it
 * cannot play, cannot be seen, and is not a video on the page. A roster with
 * eight filmed players must not fetch eight highlight reels to show one.
 *
 * A dead id drops out of the rotation on its onError and the next player takes
 * the screen immediately, rather than leaving a blank card until the dwell timer
 * comes round. One clip in the list means no timer at all: no cycle, and a
 * caption that never churns.
 *
 * Every timing value is a CLIP_LOOK dial. That file exists to be tuned.
 */
function HeroClipCycle({ frames }) {
  // The frames still believed good. A dead id is removed rather than skipped
  // over, so the next player slides into this index and the modulo below keeps
  // the cursor in range without a second piece of state.
  const [live, setLive] = useState(frames);
  const [cursor, setCursor] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    setLive(frames);
    setCursor(0);
    setFading(false);
  }, [frames]);

  const index = live.length > 0 ? cursor % live.length : 0;
  const frame = live[index] || null;

  // Dwell, then start the fade. Re-armed on every advance.
  useEffect(() => {
    if (live.length <= 1) return undefined;
    const t = setTimeout(() => setFading(true), CLIP_LOOK.heroCycleSeconds * 1000);
    return () => clearTimeout(t);
  }, [index, live.length]);

  // The far side of the fade: swap the source and come back up.
  useEffect(() => {
    if (!fading) return undefined;
    const t = setTimeout(() => {
      setCursor((c) => c + 1);
      setFading(false);
    }, CLIP_LOOK.heroCrossfadeMs);
    return () => clearTimeout(t);
  }, [fading]);

  // Warm the NEXT file only, on an element that never joins the document.
  useEffect(() => {
    if (live.length <= 1) return undefined;
    const next = live[(index + 1) % live.length];
    if (!next) return undefined;
    const warm = document.createElement("video");
    warm.preload = "auto";
    warm.muted = true;
    warm.src = next.url;
    return () => warm.removeAttribute("src");
  }, [index, live]);

  if (!frame) return null;

  return (
    <div
      data-testid="hero-clip-cycle"
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        opacity: fading ? 0 : 1,
        transition: `opacity ${CLIP_LOOK.heroCrossfadeMs}ms ease-in-out`,
      }}
    >
      <ClipBackdrop
        url={frame.url}
        focus={frame.focus}
        onError={(dead) => setLive((cur) => cur.filter((f) => f.url !== dead))}
      />
      {/* Names whoever is on screen, so the footage reads as this roster rather
          than as a stock highlight. Tiny and muted on purpose: it is a label on
          atmosphere, not a line of the report. */}
      <div
        data-testid="hero-clip-caption"
        style={{
          position: "absolute",
          right: 12,
          bottom: 10,
          fontSize: SZ.micro,
          color: C.text.faint,
          letterSpacing: "0.3px",
        }}
      >
        {frame.pick.player}
      </div>
    </div>
  );
}

/** How the copy says a format out loud. The dists cell for 2QB is superflex. */
export function formatWords(report) {
  if (report.format === "2qb") return "superflex";
  return report.benchmarkFormat === "superflex" ? "superflex" : "single QB";
}

/**
 * "beats 62% of 690 real superflex teams", or nothing when there is no number.
 *
 * Re-exported from CorpusMeter rather than kept as a second copy: the meter's
 * claim line and this one are the same sentence about the same number, and the
 * extremes wording ("beats all 690", "trails all 690") has to be identical in
 * both or the card contradicts itself at the ends of the corpus.
 */
export const beatsLine = percentileClaim;

/**
 * A whole number with thousands separators: 1792 -> "1,792".
 *
 * Hand-rolled rather than toLocaleString because a season total is a number the
 * page's own copy is built around, and a runtime locale that groups on a space
 * or a full stop would change the sentence under the reader. The rest of this
 * report's figures are dollars and tenths and never reach four digits, which is
 * why this is the only place it comes up.
 */
export function groupedInt(v) {
  if (!Number.isFinite(v)) return "";
  return String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * The topline tone: green above the 60th, red below the 40th, neutral between.
 * The middle is deliberately NOT red. Half of every corpus is below its own
 * median by construction, and colouring an ordinary team as a failure is both
 * unkind and uninformative.
 */
export function percentileTone(pct) {
  if (pct == null) return C.text.secondary;
  if (pct >= 60) return C.status.success;
  if (pct < 40) return C.status.danger;
  return C.text.secondary;
}

const POS_GRADE_COLOR = {
  strong: C.status.success,
  solid: C.brand.accent,
  thin: C.status.warning,
};

/**
 * groupTitleColor and GRADE_COLOR now live on the shared card (GroupCard.jsx),
 * because the league report draws the same coloured title and the same badge.
 * Re-exported here so TeamTab's slot steppers and the tests that pin the colour
 * signature keep the import path they already have.
 */
export { groupTitleColor };

function PositionChips({ chips }) {
  const still = prefersReducedMotion();
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
      {GRADE_POSITIONS.map((pos, i) => {
        const g = chips[pos];
        const word = g && g.word ? g.word : "no projection";
        const color = (g && POS_GRADE_COLOR[g.word]) || C.text.faint;
        return (
          <span
            key={pos}
            data-testid={`chip-${pos}`}
            style={{
              fontSize: SZ.micro,
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: theme.radius.lg,
              border: `1px solid ${color}`,
              color,
              ...chipEnter(i, still),
            }}
          >
            {pos} {word}
          </span>
        );
      })}
    </div>
  );
}

/**
 * The team tab's reading of the shared card: a corpus letter grade in the
 * badge, its percentile underneath, and the percentile sentence as the note.
 *
 * 🔴 THE BADGE MEANING IS THE ONLY THING THAT DIFFERS FROM THE LEAGUE PAGE, and
 * it is supplied here rather than branched inside GroupCard. See that file's
 * header: a card that decided this for itself would have to know which report
 * it was on.
 */
function TeamGroupCard({ group, report, wide, words }) {
  return (
    <GroupCard
      group={group}
      badge={group.grade}
      badgeSub={group.percentile == null ? "no cell" : pctShown(group.percentile)}
      badgeColor={group.grade ? GRADE_COLOR(group.grade) : C.border.default}
      note={
        (group.kind === "bench"
          ? benchPercentileLine(group.percentile, group.n, words, group.key)
          : percentileLine(group.percentile, group.n, words)) ||
        /* A K or DEF card IS projected - the points beside the player are real
           and count in the headline - it simply has no corpus cell to be placed
           against, and saying "not projected" over a column of numbers would
           contradict the row underneath it. */
        (group.ungraded ? "standard scoring, not graded against the corpus" : "not projected")
      }
      hasPrices={report.hasPrices}
      wide={wide}
      rowInfo={(pick) => ({
        // The DISPLAY projection: the user's own points under the user's own
        // scoring. The percentile on the badge comes from somewhere else on
        // purpose (see rateTeam.js).
        proj: report.projection ? report.projection.perPlayer[`${pick.player}|${pick.pos}`] : null,
        rank: posRankOf(pick.player, pick.pos, report.posRanks),
      })}
    />
  );
}

/**
 * The entered ladder, played back with what the market says. Kept for the
 * unprojected case, where there are no lineup groups to draw.
 */
function ReportLadder({ report }) {
  return (
    <div>
      <div style={{ display: "flex", gap: 8, fontSize: SZ.micro, color: C.text.faint, padding: "0 0 4px" }}>
        <span style={{ minWidth: 42 }} />
        <span style={{ flex: 1 }}>player</span>
        {report.hasPrices && (
          <>
            <span style={{ width: 46, textAlign: "right" }}>paid</span>
            <span style={{ width: 52, textAlign: "right" }}>market</span>
            <span style={{ width: 52, textAlign: "right" }}>surplus</span>
          </>
        )}
      </div>
      {report.picks.map((p, i) => (
        <div
          key={`${p.player}-${i}`}
          data-testid={`ladder-row-${p.slot || p.pos}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 0",
            borderBottom: `1px solid ${C.border.default}`,
          }}
        >
          <SlotChip slot={{ label: p.slot || p.pos, pos: p.pos }} filledPos={p.pos} />
          <PlayerAvatar player={avatarPlayer(p)} size={24} />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: SZ.small,
              fontWeight: 700,
              color: C.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {p.player}
          </span>
          {report.hasPrices && (
            <>
              <span style={{ width: 46, textAlign: "right", fontSize: SZ.small, color: p.paid == null ? C.text.faint : C.text.secondary }}>
                {p.paid == null ? NONE : money(p.paid)}
              </span>
              {/* A pick no benchmark covers shows a DASH, not a zero and not a
                  blank: K, DEF and the cheap tail are not graded, a 0 would
                  read as "the market says he is worthless", and an empty cell
                  leaves the paid figure beside it looking like the whole
                  story. Same rule as the group cards. */}
              <span style={{ width: 52, textAlign: "right", fontSize: SZ.small, color: C.text.faint }}>
                {p.bench == null ? NONE : money(p.bench)}
              </span>
              <span
                style={{
                  width: 52,
                  textAlign: "right",
                  fontSize: SZ.small,
                  fontWeight: 600,
                  color:
                    p.bench == null
                      ? C.text.faint
                      : p.surplus > 0.5
                        ? C.status.success
                        : p.surplus < -0.5
                          ? C.status.danger
                          : C.text.secondary,
                }}
              >
                {p.bench == null ? NONE : signedMoney(p.surplus)}
              </span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The plain, quiet button this page uses for navigation rather than for an
 * action on the report. Defined once because the teaser and the hero card both
 * draw the same "Edit my roster", and two copies is how they drift.
 */
const ghostBtn = {
  padding: "10px 16px",
  fontSize: SZ.small,
  fontWeight: 700,
  borderRadius: theme.radius.lg,
  border: `1px solid ${C.border.default}`,
  background: "transparent",
  color: C.text.secondary,
  cursor: "pointer",
};

/**
 * The peek: two numbers on a plain card, and nothing else.
 *
 * 🔴 WHAT IS DELIBERATELY NOT HERE. No meter, no clip, no position chips, no
 * market line and no grade badge. Ben asked for the gate to come "before you
 * see the animation and everything", and a teaser that draws the percentile
 * METER has already spent the thing the unlock is supposed to deliver.
 *
 * The percentile line names its own population, so this card makes no claim it
 * cannot back on its own: "beats 63% of 324 real single QB teams" is a complete
 * sentence about a real corpus, counted off the artifact rather than typed into
 * prose. The one thing it does NOT restate is the two-lens note, which explains
 * why the points and the percentile are scored differently; that note earns its
 * place beside the meters and the per-player points it actually reconciles, and
 * printing it over a two-line peek would be a paragraph of methodology on a
 * card carrying one number.
 *
 * "Edit my roster" stays, because it is navigation and not report content.
 * Somebody who typed the wrong Josh must be able to go back and fix him without
 * handing over an address first.
 */
function TeaserCard({ report, week1Line, projected, onEdit }) {
  return (
    <div data-testid="rate-teaser" style={{ ...card, marginBottom: 14 }}>
      {projected ? (
        <>
          <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.3 }}>
            {report.week1.toFixed(1)} projected points in week 1
          </div>
          {week1Line && (
            <div style={{ fontSize: SZ.body, color: C.text.secondary, marginTop: 2 }}>
              {week1Line}
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: SZ.body, fontWeight: 700 }}>
          No projection yet: add players the projection set carries and the lineup fills in.
        </div>
      )}
      {/* 🔴 ABSENT WHEN THERE IS NOTHING TO EDIT. A seat opened from a fetched
          Sleeper league is somebody else's roster read off the API - there is no
          entry form behind it and a button promising one would be a dead end.
          The caller supplies no handler in that case and the affordance is
          simply not drawn; the way back to the league is drawn instead. */}
      {onEdit && (
        <div style={{ marginTop: 12 }}>
          <button onClick={onEdit} style={ghostBtn}>
            Edit my roster
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The panel that stands where the report will be.
 *
 * The copy is honest on purpose and the tone is the ask, not the threat: the
 * report IS free, it wants an address, and the sentence below says exactly what
 * arrives rather than gesturing at "more insights". Nothing here is a countdown,
 * a fake blur, a partial reveal or a claim that the offer expires.
 *
 * Every number in the copy is counted off the report object already in memory,
 * which is also the proof that the promise is about something that exists.
 */
function GatePanel({ report, groups, onUnlock, cellWords, cellN }) {
  const cards = groups.starters.length + groups.bench.length;
  return (
    <div data-testid="rate-gate" style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: SZ.sub,
          fontWeight: 800,
          margin: "4px 0 8px",
        }}
      >
        {/* 🔴 ONE NUMBER ON THE SCREEN, AND IT IS THE ONE THE RATING USED.
            This heading used to quote report.corpus.nTeams while the hero two
            inches above quoted the size-bucket count, so the page showed 586
            and 582 for the same idea and neither was labelled. The gate now
            takes the SAME cell the percentile was computed against, passed in
            rather than recomputed, so the two cannot drift apart again. */}
        Enter your email address to unlock your full team rating against{" "}
        {fmtCount(cellN)} real {cellWords} auction teams
      </div>
      <div
        style={{
          fontSize: SZ.small,
          color: C.text.secondary,
          lineHeight: 1.6,
          marginBottom: 12,
        }}
      >
        {/* Short, but still specific about what actually arrives. The promise
            has to name something that exists; "more insights" would not. */}
        {cards} graded position {cards === 1 ? "card" : "cards"}, projected points
        and position rank for every player
        {report.hasPrices ? ", and a market grade on every price you paid." : "."}
      </div>
      <EmailAsk
        report={report}
        testId="rate-gate-ask"
        source="rate_gate"
        title="Enter your email address"
        blurb="Free, and the full report opens here for good."
        cta="Unlock my full rating"
        doneText="Unlocked. Your report is below."
        /* 🔴 ONE FIELD. This form is the first thing between a reader and
           their own report, so it asks for one thing. The draft date bought
           nothing the report needs, and the post carries draftAt null, which
           the signup endpoint already files as unscheduled. */
        askDate={false}
        onSubmitted={onUnlock}
      />
    </div>
  );
}

/**
 * What a graded room's report says about where its numbers went.
 *
 * 🔴 THIS REPLACED AN ASK (Sep 2 2026). The line here used to invite the reader
 * to go to /submit and add their room to the data. A reader who handed over a
 * whole draft board has already given it; the room now sends itself the moment
 * it grades (src/utils/autoSubmitRoom.js), so the page states that in one line
 * instead of asking for a thing it just did.
 *
 * 🔴 AND IT IS DRAWN ONLY WHERE IT IS TRUE. Both entry paths send now, so the
 * gate is no longer "was this pasted" but "did this room have prices": a roster
 * typed without dollars posts nothing and must not carry a line claiming its
 * numbers joined the data. The caller decides (`sharesData`), because the report
 * cannot see whether the rest of the room was priced.
 *
 * "Anonymous" is a claim about the payload and it is one the payload keeps:
 * seat index, players, positions and prices, with no team names, no league
 * name, no address and no id. See api/rate-room.js.
 */
export function DataFootnote() {
  return (
    <div
      data-testid="data-footnote"
      style={{ marginTop: 16, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}
    >
      Graded rooms join the anonymous market data.
    </div>
  );
}

export default function TeamReportView({
  report,
  leagueName,
  usedSavedLeague,
  onEdit,
  onOpenLeague,
  onUnlocked,
  /* True when this report's room auto-sends, which is every priced room on
     either entry path. See DataFootnote: the line it draws is a statement of
     fact, not an offer, so it is drawn where the fact holds and nowhere else. */
  sharesData = false,
  /* "paste" | "sleeper" | "manual", for the gate event only. Null when the
     caller has no opinion: the funnel records no source rather than a guessed
     one, which is the same absent-never-defaulted rule the page runs on. */
  source = null,
}) {

  /* 🔴 A REPORT OPENS AT THE TOP OF THE PAGE. The surfaces on /rate swap by
     state rather than by navigation, so the browser keeps whatever scroll
     offset the previous screen had — and the screens before a report are the
     tall ones (a long paste box, a fourteen-seat roster), so a reader who
     scrolled to press the button landed halfway down their own grade.

     MOUNT-SCOPED ON PURPOSE, and the empty dep array is the whole guard: this
     fires when the surface comes into view, never on a re-render. Typing in a
     field, toggling a chip or expanding a row must not yank the page upward.
     Every transition that should scroll is a real mount here — the room and
     the seat are mutually exclusive branches, so opening a team, coming back
     to the league, and swapping tabs all unmount one and mount the other.

     try/catch guards a HOST without scrollTo at all (a non-browser renderer),
     not jsdom: jsdom defines the function and LOGS "Not implemented" rather
     than throwing, so nothing here can catch that. Suite quiet comes from the
     stub in src/setupTests.js instead. */
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* no scrollTo (jsdom, or a non-browser host) — nothing to do */
    }
  }, []);

  const words = formatWords(report);
  const still = prefersReducedMotion();
  // 🔴 THE TWO PERCENTILES ALREADY COUNT UP — inside CorpusMeter, on the shared
  // hook, and they have since the meter shipped. This is the HEADLINE number,
  // which did not: the biggest figure on the page used to snap into place while
  // the corpus climbed underneath it. Same loop, a much shorter dial, so it is
  // done long before the meter and never races it.
  const week1Shown = useCountUp(report.week1 || 0, RATE_MOTION.countUpMs, still);
  // The same dial, so the two numbers in the hero land together rather than one
  // ticking beside a number that snapped into place.
  const seasonShown = useCountUp(report.season || 0, RATE_MOTION.countUpMs, still);
  const sent = useRef(false);
  // Read once, before the first paint, so an already-unlocked reader never sees
  // the gate flash past on the way to the content they paid for.
  const [unlocked, setUnlocked] = useState(() => isRateUnlocked());
  const gateSeen = useRef(false);

  // One event per report. The ref guards StrictMode's double effect run, the
  // same way the league tab's deep-link grade does.
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    captureTeamRated({
      format: report.format,
      teams: report.teams,
      budget: report.budget,
      hasPrices: report.hasPrices,
      usedSavedLeague: !!usedSavedLeague,
    });
  }, [report, usedSavedLeague]);

  // One rate_gate_shown per report that actually showed one. The ref guards
  // StrictMode's double effect run, the same way the rating event above does.
  useEffect(() => {
    if (unlocked || gateSeen.current) return;
    gateSeen.current = true;
    captureRateGateShown({ format: report.format, hasPrices: report.hasPrices });
  }, [unlocked, report]);

  function unlock() {
    markRateUnlocked();
    setUnlocked(true);
    captureRateGateUnlocked({ format: report.format, hasPrices: report.hasPrices, source });
    // The page around this report may have things that belong after it - the
    // "add another team" card does - and it cannot see a state that lives in
    // here. Optional, so every existing caller is unaffected.
    if (onUnlocked) onUnlocked();
  }

  // Two columns is the phone budget (design addendum). A wide viewport shows
  // the price trio inline instead of behind a tap.
  const [wide, setWide] = useState(
    typeof window === "undefined" ? true : window.innerWidth > 820
  );
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth > 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 🔴 THE SAME SPLIT THE ROOM CARD DRAWS, from the same function and the same
  // three fields. The seat report already cuts this roster into Starters and
  // Bench cards below, off splitRoster; this is what that same cut cost. It is
  // computed here rather than in the section so the absent case (no dollars, no
  // scoring, nothing matched) draws nothing at all rather than an empty line —
  // leagueSpend.js carries the contract.
  const split = useMemo(
    () =>
      starterBenchSpend({
        picks: report.picks,
        proj: report.projection,
        hasPrices: report.hasPrices,
      }),
    [report.picks, report.projection, report.hasPrices]
  );

  const hero = heroPick(report);
  // Reduced motion and a roster nobody filmed are the same answer: no video
  // element at all, so nothing can autoplay, buffer or fail, and no caption
  // appears either. That is the static card exactly as it shipped.
  const frames = useMemo(
    () => (prefersReducedMotion() ? [] : heroCycle(report)),
    [report]
  );
  // Lifted above the backdrop, exactly as the live board's card does it. With
  // no clip this contributes nothing and the markup is the static card.
  const lift = frames.length > 0 ? { position: "relative", zIndex: 1 } : null;

  // The week1 claim names the population it was actually measured against,
  // which is the size bucket when one exists and the pooled cell when not.
  // Null at a size the corpus cannot cover — there is no pooled level to fall
  // to any more. Two populations, two claims, each saying exactly what it is:
  // the middle one is a real cell of this league's size that is simply not
  // split by starter count, so it claims the size and nothing more — no
  // "lineups", which is the word the exact cell earns by matching the starter
  // count too.
  const cell = report.week1Cell;
  const cellWords = cell ? `${cell.teams}-team ${words}` : words;
  const week1Line = cell ? beatsLine(report.week1Percentile, cell.n, cellWords) : null;
  const week1Caption = !cell
    ? null
    : cell.matchedOn === "size"
      ? `${cell.n} real ${cell.teams}-team ${words} lineups. You land here.`
      : `${cell.n} real ${cell.teams}-team teams. You land here.`;
  // The market claim counts the size cell it was actually measured against, not
  // the pooled corpus total.
  // 🔴 SAID ONCE, NOT SIX TIMES. When the corpus has no cells at this league
  // size, the market grade and every position badge go null together. That is
  // one fact about the data, so it gets one sentence and a route — not six
  // blank chips and an empty grade badge for the reader to piece together.
  const noCorpusAtSize = !(report.corpusAtSize && report.corpusAtSize.any);
  const surplusCell = report.surplusCell;
  const surplusLine = surplusCell
    ? beatsLine(report.surplusPercentile, surplusCell.n, `${surplusCell.teams}-team ${words}`)
    : null;
  const projected = report.week1 != null && report.projection && report.projection.starterMatchedCount > 0;
  // 🔴 THE SEASON TOTAL IS THE SAME LINEUP AND THE SAME RULES. Its percentile
  // comes from the corpus's own seasonByTeams cell — the size cut, never a
  // pooled one — and it is simply ABSENT when the corpus has no cell for this
  // league size, exactly as week1Line is. A season number with a percentile
  // against a population that does not exist is the one thing this line must
  // never be, so the claim is built from the cell or it is not built.
  const seasonCell = report.seasonCell;
  const seasonLine = seasonCell
    ? beatsLine(report.seasonPercentile, seasonCell.n, `${seasonCell.teams}-team ${words}`)
    : null;

  // 🔴 ABSENT, NOT HIDDEN, AND NOW STRUCTURALLY SO. The locked page is a
  // different return, so there is no branch of the markup below that could be
  // reached with the gate still up: not the hero card, not a meter, not a
  // group card, not the market grade. A reader with devtools open finds a
  // teaser and a form, because that is all the component rendered.
  if (!unlocked) {
    return (
      <div>
        <TeaserCard
          report={report}
          week1Line={week1Line}
          projected={projected}
          onEdit={onEdit}
        />
        <GatePanel
          report={report}
          groups={report.groups}
          onUnlock={unlock}
          /* The hero's own numbers, handed down rather than recomputed. */
          cellWords={cellWords}
          cellN={cell ? cell.n : null}
        />
        {sharesData && <DataFootnote />}
      </div>
    );
  }

  return (
    <div>
      <div
        data-testid="hero-card"
        style={{ ...card, marginBottom: 14, position: "relative", overflow: "hidden" }}
      >
        {frames.length > 0 && <HeroClipCycle frames={frames} />}

        <div style={{ fontSize: SZ.small, color: C.text.secondary, ...lift }}>
          {/* Same rule as the room report's heading: the league's own name gets
              the accent, the "no name here" fallback does not. */}
          {leagueName ? (
            <span data-testid="seat-league-name" style={leagueNameText}>
              {leagueName}
            </span>
          ) : (
            "Your team"
          )}{" "}
          · {report.teams} teams · ${report.budget} budget
        </div>

        {projected ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "6px 0 2px", ...lift }}>
            {hero && <PlayerAvatar player={avatarPlayer(hero)} size={40} />}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.3 }} data-testid="hero-week1">
                {week1Shown.toFixed(1)} projected points in week 1
              </div>
              {week1Line && (
                <div style={{ fontSize: SZ.body, color: C.text.secondary }}>{week1Line}</div>
              )}
              {/* 🔴 THE SEASON, BESIDE THE WEEK. Ben, Sep 3 2026: show the
                  team's projected season points alongside the week 1 headline.
                  A notch quieter than the headline because it is the same
                  lineup read the other way rather than a competing claim, and
                  a whole number because a tenth of a point over seventeen weeks
                  is precision nobody has. */}
              <div style={{ fontSize: SZ.sub, fontWeight: 800, marginTop: 4 }} data-testid="hero-season">
                {groupedInt(seasonShown)} projected points across the season
              </div>
              {seasonLine && (
                <div style={{ fontSize: SZ.body, color: C.text.secondary }}>{seasonLine}</div>
              )}
              {hero && (
                <div style={{ fontSize: SZ.small, color: C.text.secondary }}>
                  led by <b style={{ color: C.text.primary }}>{hero.player}</b>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: SZ.body, fontWeight: 700, margin: "6px 0 2px", ...lift }}>
            No projection yet: add players the projection set carries and the lineup fills in.
          </div>
        )}

        <div style={{ fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5, marginTop: 6, ...lift }}>
          {report.benchmarkLabel}
          {report.benchmarkFormat === "1qb" &&
            ` · single QB benchmarks come from ${report.nRooms} real rooms so far: read as direction`}
          {report.format === "2qb" && " · graded against superflex prices, the closest cell to 2QB"}
        </div>

        {/* The two lenses, on the face. A reader who sees a percentile that
            does not track the points above it deserves to know why before
            they wonder whether one of the two is broken. */}
        <div
          data-testid="lens-note"
          style={{ fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5, marginTop: 4, ...lift }}
        >
          your points use your scoring; percentiles score every team the same way
          {report.comparisonLens ? ` (${report.comparisonLens})` : ""}
        </div>

        {/* The two numbers the whole page is about, each drawn ON the corpus
            it is measured against. Value is null without prices, and then only
            the projection strip shows: no placeholder, no zero, no implied
            grade. */}
        <div style={lift || undefined}>
          {report.week1Percentile != null && (
            <CorpusMeter
              testId="topline-projection"
              values={report.corpusCurves.week1}
              // 🔴 THE REFERENCE VALUE, NOT THE DISPLAYED ONE. The bars are the
              // corpus scored under the reference lens, so the marker has to be
              // this team scored the same way or the chart disagrees with its
              // own axis. The headline above still shows the user's own points.
              value={report.week1Ref}
              percentile={report.week1Percentile}
              heading="projection percentile"
              claim={week1Line}
              caption={week1Caption}
              tone={percentileTone(report.week1Percentile)}
            />
          )}
          {report.surplusPercentile != null && (
            <CorpusMeter
              testId="topline-value"
              compact
              values={report.corpusCurves.surplus}
              value={report.surplusScaled}
              percentile={report.surplusPercentile}
              heading="value percentile"
              claim={surplusLine}
              caption={`${surplusCell ? surplusCell.n : 0} real ${surplusCell ? `${surplusCell.teams}-team ` : ""}${words} teams. You land here.`}
              tone={percentileTone(report.surplusPercentile)}
            />
          )}
        </div>

        {noCorpusAtSize ? (
          <div style={{ ...card, marginTop: 10, ...lift }} data-testid="no-corpus-at-size">
            <div style={{ fontSize: SZ.body, fontWeight: 700, marginBottom: 4 }}>
              Not enough {report.teams}-team {words} auctions in the data yet
            </div>
            <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}>
              Every percentile on this report &mdash; the projection, the market grade and the
              position badges &mdash; places you against real rooms of your league&rsquo;s size, and
              there are not enough {report.teams}-team {words} rooms in the data yet to do that
              honestly. Grading you against the other sizes would measure your league&rsquo;s size
              and not your roster. Your projected points are still yours and still shown.
            </div>
            {onOpenLeague && (
              <button
                onClick={onOpenLeague}
                style={{ ...ghostBtn, marginTop: 10 }}
                data-testid="open-league-from-report"
              >
                Grade the whole league instead
              </button>
            )}
            <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5, marginTop: 8 }}>
              Paste the draft and every seat is graded against the others in your own room, where
              league size cancels.
            </div>
          </div>
        ) : (
          <div style={lift || undefined}>
            <PositionChips chips={report.positionChips} />
          </div>
        )}

        <div style={{ marginTop: 10, ...lift }}>
          <LineupChips positions={report.positions} />
        </div>

        {onEdit && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, ...lift }}>
            <button onClick={onEdit} style={ghostBtn}>
              Edit my roster
            </button>
          </div>
        )}
      </div>

      {report.hasPrices ? (
        <div style={{ ...card, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* 🔴 NO LETTER WITHOUT A POPULATION OF THIS LEAGUE'S SIZE. The
                dollars, the best buy and the worst overpay are per-pick and
                stay; only the corpus percentile and the letter it produces
                depend on a size cell, and where there is none the card says so
                instead of grading against the pooled curve. A pooled letter
                here is not a rougher answer — it is a reading of the league's
                size wearing a roster's clothes. */}
            {report.grade && (
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: theme.radius.lg,
                  background: C.bg.deep,
                  border: `1px solid ${C.border.default}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  fontWeight: 900,
                  color: GRADE_COLOR(report.grade),
                  flexShrink: 0,
                  ...gradePop(0, still),
                }}
                data-testid="team-grade"
              >
                {report.grade}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: SZ.body, fontWeight: 700 }}>
                market surplus {signedMoney(report.surplus)} on {money(report.spent)} spent
              </div>
              {surplusLine ? (
                <div style={{ fontSize: SZ.small, color: C.text.secondary }}>{surplusLine}</div>
              ) : noCorpusAtSize ? null : (
                <div
                  style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}
                  data-testid="no-size-grade"
                >
                  Not enough {report.teams}-team {words} auctions in the corpus yet to put a letter
                  on this. Fewer teams means less money chasing the same players, so grading you
                  against the other sizes would measure your league's size and not your roster. The
                  surplus above, your best buy and your worst overpay are unaffected.
                </div>
              )}
            </div>
          </div>
          {report.bestBuy && (
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
              <PlayerAvatar player={avatarPlayer(report.bestBuy)} size={40} />
              <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5, minWidth: 0 }}>
                Best buy: <b style={{ color: C.text.primary }}>{report.bestBuy.player}</b> at{" "}
                {money(report.bestBuy.paid)}, market {money(report.bestBuy.bench)}.
              </div>
            </div>
          )}
          {report.worstOverpay && (
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
              <PlayerAvatar player={avatarPlayer(report.worstOverpay)} size={40} />
              <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5, minWidth: 0 }}>
                Worst overpay: <b style={{ color: C.text.primary }}>{report.worstOverpay.player}</b> at{" "}
                {money(report.worstOverpay.paid)}, market {money(report.worstOverpay.bench)}.
              </div>
            </div>
          )}
        </div>
      ) : report.pricedCount > 0 ? (
        /* 🔴 A PARTLY PRICED ROSTER MUST SAY SO. Prices are all or nothing at
           the graded positions, because summing real surpluses against blanks
           would read as a mediocre team rather than as an incomplete entry. The
           failure mode that leaves is worse than the one it prevents: a user
           who typed eight prices and missed one watched the whole market
           section vanish with no explanation. It never disappears silently
           again; it says what is missing and what will bring it back. */
        <div style={{ ...card, marginBottom: 14 }} data-testid="mixed-prices">
          <div style={{ fontSize: SZ.body, fontWeight: 700, marginBottom: 4 }}>
            add a price to every starter and the market grade unlocks
          </div>
          <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}>
            {report.pricedCount} of {report.priceableCount} priced so far. Everything above still
            comes from your projections and is unaffected. Every QB, RB, WR and TE needs a price
            before the grade can be honest; kicker and defense never do.
          </div>
        </div>
      ) : (
        <div style={{ ...card, marginBottom: 14 }} data-testid="no-prices">
          <div style={{ fontSize: SZ.body, fontWeight: 700, marginBottom: 4 }}>
            No prices, so no market grade
          </div>
          <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}>
            Everything above comes from projections alone. Add what you paid for each player and this
            team also gets a market grade, a best buy and a worst overpay against real 2026 auction
            prices. Kicker and defense prices are never needed.
          </div>
        </div>
      )}

      {report.groups.starters.length > 0 ? (
        <>
          {/* Above the heading it explains: the cards below are the split, this
              is what the split cost. Same words as the room card, drawn by the
              same component so the two cannot word it differently. */}
          <StarterSplitLine
            split={split}
            testId="seat-starter-split"
            style={{ margin: "4px 0 8px" }}
          />
          <div style={{ fontSize: SZ.sub, fontWeight: 800, margin: "4px 0 8px" }}>Starters</div>
          {report.groups.starters.map((g) => (
            <TeamGroupCard key={`s-${g.key}`} group={g} report={report} wide={wide} words={words} />
          ))}
          {report.groups.bench.length > 0 && (
            <>
              <div style={{ fontSize: SZ.sub, fontWeight: 800, margin: "14px 0 8px" }}>Bench</div>
              {report.groups.bench.map((g) => (
                <TeamGroupCard key={`b-${g.key}`} group={g} report={report} wide={wide} words={words} />
              ))}
            </>
          )}
        </>
      ) : null}

      {/* No lineup to group by, so the roster shows as the flat ladder it was
          entered on rather than as a set of empty graded cards. Gated with the
          cards, because it is the same content in a plainer shape. */}
      {report.groups.starters.length === 0 && (
        <div style={{ ...card, marginBottom: 14 }}>
          <ReportLadder report={report} />
        </div>
      )}

      {/* 🔴 THE CORE STATS STAY, THE WALL GOES BEHIND A TAP (Sep 3 2026).
          What was here was thirty lines of methodology under a report on a
          phone: correct, load-bearing, and read by nobody because it arrived as
          a paragraph. Three lines say what the grade was measured against, and
          the ENTIRE text below is unchanged and one tap away.

          🔴 NOTHING IS DELETED AND NOTHING IS CONDITIONAL. A <details> keeps
          every sentence in the DOM whether it is open or shut, so the page is
          still readable to a screen reader, still searchable with the browser's
          own find, and still holds every caveat the numbers depend on. A
          version that rendered the text only when expanded would be a page that
          shows its own reasoning only to people who already suspect it.

          Counts come off the report object, never out of prose. Same rule the
          text inside follows. */}
      <div style={{ ...fineText, marginTop: 0, marginBottom: 16 }} data-testid="grading-basis">
        <div>
          Graded against {report.corpus.nTeams} real teams from {report.corpus.nRooms} real 2026
          auctions, all re-scored under one common lens ({report.comparisonLens}).
        </div>
        <div style={{ marginTop: 4 }}>
          Projected points come from Mock1 house projections ({projectionIndex().size} players)
          scored with your league's own scoring settings, started in the best lineup your roster
          slots allow.
        </div>
        <div style={{ marginTop: 4 }}>
          Every badge number is a percentile against that cell, not a score out of 100, and the
          grade floor is D.
        </div>

        <details data-testid="grading-details" style={{ marginTop: 8 }}>
          <summary
            style={{
              cursor: "pointer",
              color: C.brand.accent,
              fontWeight: 700,
              /* A tap target on a phone, not a 12px word. */
              padding: "6px 0",
            }}
          >
            How grading works
          </summary>
          <div style={{ marginTop: 6 }}>
            Projected points come from Mock1 house projections ({projectionIndex().size} players) scored
            with your league's own scoring settings, started in the best lineup your roster slots allow.
            Kickers and defenses come from a separate table of all {KD_TEAM_COUNT} teams and use standard
            kicker and defense scoring, so your league's own settings do not move them. Every percentile
            on this page is computed differently on
            purpose: your roster and all {report.corpus.nTeams} real teams are re-scored under one common
            lens ({report.comparisonLens}) first, because a comparison whose two sides use different
            scoring rules is not a comparison. Positional strength compares your projected points at each
            position with real {words} rooms: the sum of your best projected players at that position,
            counting one more than that position's own starting slots, placed against{" "}
            {report.corpus.nTeams} real teams from {report.corpus.nRooms} rooms.
            {report.hasPrices &&
              " Per pick surplus is the market price minus what you paid, in your league's dollars;" +
                " cheap picks outside the market table grade as even money, and show a dash rather" +
                " than a number."}
            {" "}
            Every badge number is a percentile against that cell, not a score out of
            100: the grade floor is D and the middle of the corpus is not marked as a
            failure. FLEX and SUPERFLEX cards are graded the same way, against real
            teams' own flex slots plus one eligible depth piece, and are kept apart
            because a superflex slot holds a quarterback and a flex slot does not.
            {report.groups.bench.length > 0 &&
              " A bench card is measured against real BENCH players at that position," +
                " not against real teams: its badge is the average projected points per" +
                " game of the players you did not start, placed among every player the" +
                " corpus benched. A bench player the projection set does not carry is" +
                " shown in the rows and left out of that average."}
          </div>
        </details>
      </div>

      {/* 🔴 NO SECOND ASK. The reader of this half of the page has already
          given an address; a card at the bottom offering to take another one is
          a page that does not remember what its own reader just did. The league
          report keeps its ask, because nothing there was ever gated. */}
      {sharesData && <DataFootnote />}
    </div>
  );
}
